import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { EventUnionWithPermissions } from '../../types/events.types';
import { useAppQuery } from '../use-app-query.hook';
import { pendingEventsStore } from './pending-events.store';

const getEvent = ({ companyId, eventId }: { companyId: string; eventId: string }) =>
  ApiService.get<EventUnionWithPermissions>({
    endpoint: BackendRoute.COMPANIES,
    routePath: [companyId, 'events', eventId],
  });

export const useEvent = ({ companyId, eventId }: { companyId: string; eventId: string }) => {
  const queryClient = useQueryClient();

  // While this event's queued EVENT_MUTATION apply is in flight the detail endpoint can only
  // answer with pre-apply data, and that answer would then sit in the cache under this key. Any
  // unrelated GET_EVENT invalidation during the pending window (a vault file add/remove, a
  // reopen) would otherwise re-poison it. Fetching resumes when useEvents settles the mutation
  // and resets this key — already-cached data is still served so the closing sheet does not
  // flash its skeleton mid-animation.
  const hasPendingMutation = pendingEventsStore((state) =>
    [
      ...(state.pendingEditsByCompany[companyId] ?? []),
      ...(state.pendingDeletesByCompany[companyId] ?? []),
    ].some((pending) => pending.eventId === eventId),
  );

  const { data: event, isLoading } = useAppQuery<EventUnionWithPermissions>({
    queryKey: [QueryKey.GET_EVENT, companyId, eventId],
    queryFn: () => getEvent({ companyId, eventId }),
    enabled: !!companyId && !!eventId && !hasPendingMutation,
    refetchOnWindowFocus: false,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_EVENT] });
  }, [queryClient]);

  return {
    invalidateQuery,
    event,
    isLoading,
  };
};

// Imperative single-event read for handlers that need the full event (the timeline list projection
// omits item relations such as stakeholder and share class).
export const useFetchEvent = () => {
  const queryClient = useQueryClient();

  const fetchEvent = useCallback(
    ({ companyId, eventId }: { companyId: string; eventId: string }) =>
      queryClient.fetchQuery<EventUnionWithPermissions>({
        queryKey: [QueryKey.GET_EVENT, companyId, eventId],
        queryFn: () => getEvent({ companyId, eventId }),
        staleTime: 0,
      }),
    [queryClient],
  );

  return { fetchEvent };
};
